import { useEffect, useRef } from "react";

/**
 * useTracking
 * Tracks both GTM and Contentsquare scroll events and provides a click handler.
 *
 * @param {string} sectionName - Section/component name
 * @param {number} [threshold=0.4] - IntersectionObserver threshold
 */
export default function useTracking(sectionName, threshold = 0.4) {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // ✅ GTM Scroll Event
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "section_scroll",
            section_name: sectionName,
            timestamp: new Date().toISOString(),
          });

          // ✅ Contentsquare Scroll Event
          if (window._uxa) {
            window._uxa.push(["trackAction", `${sectionName}_viewed`]);
          }

          console.log(`Tracked Scroll: ${sectionName}`);
        }
      },
      { threshold }
    );

    if (ref.current) observer.observe(ref.current);

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [sectionName, threshold]);

  /**
   * Call this on any clickable element inside this section
   * @param {string} actionName - Name for click event
   */
  const handleClick = (actionName) => {
    // ✅ GTM Click
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "element_click",
      section_name: sectionName,
      action_name: actionName,
      timestamp: new Date().toISOString(),
    });

    // ✅ Contentsquare Click
    if (window._uxa) {
      window._uxa.push(["trackAction", actionName]);
    }

    console.log(`Tracked Click: ${actionName} in ${sectionName}`);
  };

  return { ref, handleClick };
}
